import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Api, setUnauthorizedHandler } from '../application/panelApi';
import type { AutopilotState } from '../features/autopilot';
import type { Cluster, ControlBuild } from '../features/clusters';
import type { PfTask } from '../domain/positions';
import type { ScenarioLike } from '../domain/scenarioLanes';
import { applyStoredTheme, getTheme, toggleTheme as toggleThemeLib, type ThemeName } from '../lib/theme';

export type PageId =
  | 'search'
  | 'neural'
  | 'proxy'
  | 'launch'
  | 'servers'
  | 'users'
  | 'logs'
  | 'profiles'
  | 'settings';

export type PanelRole = string;

export type SearchEngine = 'bing' | 'google';
export type NavScope = SearchEngine;

export type UiState = {
  active: PageId;
  subpage: 'overview' | 'positions' | 'autopilot' | 'scenarios' | 'manual' | 'nodes' | 'telegram' | 'password' | 'reset';
  scope: NavScope;
  taskFilter: string;
  statusFilter: string;
  searchEngine: SearchEngine;
  sortKey: string;
  sortDir: number;
  pageSize: number;
  page: number;
  warmupStartIds?: string[];
};

const UI_KEY = 'bingcore.ui';
const AUDIT_KEY = 'bc_audit';

const PAGE_IDS: PageId[] = [
  'search',
  'neural',
  'proxy',
  'launch',
  'servers',
  'users',
  'logs',
  'profiles',
  'settings',
];

const LEGACY_PAGE: Record<string, PageId> = {
  tasks: 'search',
  neuro: 'neural',
  positions: 'search',
  scenarios: 'search',
  clicktest: 'search',
  assessment: 'search',
  clusters: 'servers',
  warmup: 'launch',
  autopilot: 'launch',
};

const DEFAULT_UI: UiState = {
  active: 'search',
  subpage: 'overview',
  scope: 'bing',
  taskFilter: '',
  statusFilter: 'all',
  searchEngine: 'bing',
  sortKey: 'site',
  sortDir: 1,
  pageSize: 25,
  page: 0,
};

function normalizePage(active: unknown): PageId {
  if (typeof active === 'string' && PAGE_IDS.includes(active as PageId)) return active as PageId;
  if (typeof active === 'string' && LEGACY_PAGE[active]) return LEGACY_PAGE[active];
  return 'search';
}

const NAV_SCOPES: NavScope[] = ['bing', 'google'];

function scopePatch(scope: NavScope): Partial<UiState> {
  return { scope, searchEngine: scope };
}

function parseRoute(pathname: string): Partial<UiState> | null {
  const [, rawScope, page, section] = pathname.split('/');
  const scope = rawScope === 'all' ? 'bing' : rawScope;
  if (!NAV_SCOPES.includes(scope as NavScope)) return null;
  const active = normalizePage(page);
  const subpage = active === 'search'
    ? section === 'positions' ? 'positions' : 'overview'
    : active === 'launch'
      ? section === 'scenarios' || section === 'manual' ? section : 'autopilot'
      : active === 'settings'
        ? section === 'telegram' || section === 'password' || section === 'reset' ? section : 'nodes'
      : 'overview';
  return { ...scopePatch(scope as NavScope), active, subpage };
}

function routePath(scope: NavScope, page: PageId, subpage: UiState['subpage']): string {
  return `/${scope}/${page}${page === 'search' || page === 'launch' || page === 'settings' ? `/${subpage}` : ''}`;
}

export type AuditEntry = { t: string; user: string; act: string; obj: string };

export type SettingsPayload = {
  mode?: string;
  controlPlane?: { baseDomain?: string };
  settings?: {
    telegramChatId?: string;
    telegramAllowedUserIds?: Array<string | number>;
    notifyCritical?: boolean;
    telegramBotTokenSet?: boolean;
    targetClusterIds?: string[];
    deleteAfterTargetHours?: number;
    farmSplitPct?: number;
    clickPaceEnabled?: boolean;
    clickPaceTimezone?: string;
    clickPaceStrength?: number;
    clickPaceStartHour?: number;
    clickPaceEndHour?: number;
  };
  deploy?: {
    workerConcurrency?: number | string;
    ramPerBrowserMb?: number | string;
    continuousFarm?: boolean;
  };
  quota?: { used?: number; limit?: number };
};

export type TaskStats = {
  total?: number;
  active?: number;
  plan?: number;
  ok?: number;
  failed?: number;
  sent?: number;
  miss?: number;
  error?: number;
};

type ProxyPool = { tag?: string };

type AppContextValue = {
  booting: boolean;
  user: string | null;
  userId: string | null;
  displayName: string | null;
  role: PanelRole | null;
  permissions: string[];
  hasPermission: (permission: string) => boolean;
  isSuperadmin: boolean;
  page: PageId;
  ui: UiState;
  theme: ThemeName;
  settingsPayload: SettingsPayload | null;
  controlMode: string;
  baseDomain: string;
  autopilot: AutopilotState | null;
  tasks: PfTask[];
  taskStats: TaskStats | null;
  clusters: Cluster[];
  controlBuild: ControlBuild | null;
  scenarios: ScenarioLike[];
  proxyPools: ProxyPool[];
  proxyMeta: { totalAddresses: number; openPools: number };
  auditLog: AuditEntry[];
  pendingLogTaskId: string | null;
  setPendingLogTaskId: (id: string | null) => void;
  goto: (page: PageId, scope?: NavScope) => void;
  setUi: (patch: Partial<UiState>) => void;
  setSearchEngine: (engine: SearchEngine) => void;
  toggleTheme: () => void;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshSettings: () => Promise<SettingsPayload>;
  refreshAutopilot: () => Promise<void>;
  refreshTasks: () => Promise<void>;
  refreshClusters: () => Promise<void>;
  refreshScenarios: () => Promise<void>;
  refreshProxy: () => Promise<void>;
  setAutopilot: (value: AutopilotState | null) => void;
  setTasks: (tasks: PfTask[]) => void;
  setTaskStats: (stats: TaskStats | null) => void;
  setClusters: (clusters: Cluster[]) => void;
  setControlBuild: (build: ControlBuild | null) => void;
  audit: (act: string, obj?: string) => void;
  autopilotLocked: boolean;
};

const AppContext = createContext<AppContextValue | null>(null);

function loadStoredUi(): UiState {
  try {
    const raw = localStorage.getItem(UI_KEY);
    if (!raw) return { ...DEFAULT_UI };
    const parsed = { ...DEFAULT_UI, ...JSON.parse(raw) };
    if (parsed.searchEngine !== 'google') parsed.searchEngine = 'bing';
    parsed.active = normalizePage(parsed.active);
    if ((parsed.scope as string) === 'all' || !NAV_SCOPES.includes(parsed.scope)) {
      parsed.scope = parsed.searchEngine;
    }
    return parsed;
  } catch {
    return { ...DEFAULT_UI };
  }
}

function loadUi(): UiState {
  return { ...loadStoredUi(), ...parseRoute(window.location.pathname) };
}

function loadAudit(): AuditEntry[] {
  try {
    const raw = sessionStorage.getItem(AUDIT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [booting, setBooting] = useState(true);
  const [user, setUser] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [role, setRole] = useState<PanelRole | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [ui, setUiState] = useState<UiState>(() => loadUi());
  const [theme, setThemeState] = useState<ThemeName>(() => applyStoredTheme());
  const [settingsPayload, setSettingsPayload] = useState<SettingsPayload | null>(null);
  const [controlMode, setControlMode] = useState('control');
  const [baseDomain, setBaseDomain] = useState('');
  const [autopilot, setAutopilot] = useState<AutopilotState | null>(null);
  const [tasks, setTasks] = useState<PfTask[]>([]);
  const [taskStats, setTaskStats] = useState<TaskStats | null>(null);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [controlBuild, setControlBuild] = useState<ControlBuild | null>(null);
  const [scenarios, setScenarios] = useState<ScenarioLike[]>([]);
  const [proxyPools, setProxyPools] = useState<ProxyPool[]>([]);
  const [proxyMeta, setProxyMeta] = useState({ totalAddresses: 0, openPools: 0 });
  const [auditLog, setAuditLog] = useState<AuditEntry[]>(() => loadAudit());
  const [pendingLogTaskId, setPendingLogTaskId] = useState<string | null>(null);

  const page = ui.active;

  const persistUi = useCallback((next: UiState) => {
    try {
      localStorage.setItem(UI_KEY, JSON.stringify(next));
    } catch {

    }
  }, []);

  const setUi = useCallback(
    (patch: Partial<UiState>) => {
      setUiState((prev) => {
        const next = { ...prev, ...patch };
        persistUi(next);
        return next;
      });
    },
    [persistUi],
  );

  const goto = useCallback(
    (next: PageId, scope?: NavScope) => {
      const patch = { active: next, subpage: next === 'launch' ? 'autopilot' as const : next === 'settings' ? 'nodes' as const : 'overview' as const };
      setUi(scope ? { ...patch, ...scopePatch(scope) } : patch);
    },
    [setUi],
  );

  const routeInitialized = useRef(false);
  const scope = ui.scope;
  useEffect(() => {
    const path = routePath(scope, page, ui.subpage);
    const first = !routeInitialized.current;
    routeInitialized.current = true;
    if (window.location.pathname === path) return;
    if (first) window.history.replaceState(null, '', path);
    else window.history.pushState(null, '', path);
  }, [page, scope, ui.subpage]);

  useEffect(() => {
    const onPop = () => {
      const route = parseRoute(window.location.pathname);
      if (route) setUi(route);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [setUi]);

  const setSearchEngine = useCallback(
    (engine: SearchEngine) => {
      setUi({ searchEngine: engine === 'google' ? 'google' : 'bing' });
    },
    [setUi],
  );

  const toggleTheme = useCallback(() => {
    setThemeState(toggleThemeLib());
  }, []);

  const audit = useCallback(
    (act: string, obj = '') => {
      const entry: AuditEntry = {
        t: new Date().toISOString(),
        user: user || '—',
        act,
        obj,
      };
      setAuditLog((prev) => {
        const next = [entry, ...prev].slice(0, 200);
        try {
          sessionStorage.setItem(AUDIT_KEY, JSON.stringify(next));
        } catch {

        }
        return next;
      });
    },
    [user],
  );

  const refreshSettings = useCallback(async () => {
    const data = (await Api.settings.get()) as SettingsPayload;
    setSettingsPayload(data);
    setControlMode(data.mode || 'control');
    setBaseDomain(data.controlPlane?.baseDomain || '');
    return data;
  }, []);

  const refreshAutopilot = useCallback(async () => {
    try {
      const data = (await Api.autopilot.get()) as { autopilot?: AutopilotState } & AutopilotState;
      setAutopilot(data.autopilot ?? data);
    } catch {
      setAutopilot(null);
    }
  }, []);

  const refreshTasks = useCallback(async () => {
    const data = (await Api.tasks.list()) as { tasks?: PfTask[]; stats?: TaskStats | null };
    setTasks(data.tasks || []);
    setTaskStats(data.stats || null);
  }, []);

  const refreshClusters = useCallback(async () => {
    const data = (await Api.clusters.list()) as { clusters?: Cluster[]; control?: ControlBuild };
    setClusters(data.clusters || []);
    if (data.control) setControlBuild(data.control);
  }, []);

  const refreshScenarios = useCallback(async () => {
    const data = (await Api.scenarios.list()) as { scenarios?: ScenarioLike[] };
    setScenarios(data.scenarios || []);
  }, []);

  const refreshProxy = useCallback(async () => {
    const data = (await Api.proxyPools.list()) as {
      pools?: ProxyPool[];
      totalAddresses?: number;
      openPools?: number;
    };
    setProxyPools(data.pools || []);
    setProxyMeta({
      totalAddresses: data.totalAddresses || 0,
      openPools: data.openPools || 0,
    });
  }, []);

  const showLogin = useCallback(() => {
    setUser(null);
    setUserId(null);
    setDisplayName(null);
    setRole(null);
    setPermissions([]);
    setBooting(false);
    document.documentElement.classList.remove('booting');
  }, []);

  const applyIdentity = useCallback(
    (me: { username?: string; displayName?: string; role?: string; permissions?: string[]; userId?: string }, fallback = '—') => {
      const name = me.username || fallback;
      setUser(name);
      setUserId(me.userId || null);
      setDisplayName(me.displayName || name);
      setRole(me.role || 'operator');
      setPermissions(me.permissions || []);
    },
    [],
  );

  const enterApp = useCallback(
    async (me: { username?: string; displayName?: string; role?: string; permissions?: string[]; userId?: string }) => {
      applyIdentity(me);
      if (me.role !== 'superadmin') {
        setUiState((previous) => {
          if (me.permissions?.includes(`${previous.active}.view`)) return previous;
          const first = PAGE_IDS.find((candidate) => me.permissions?.includes(`${candidate}.view`));
          if (!first) return previous;
          const next = { ...previous, active: first, subpage: first === 'launch' ? 'autopilot' as const : first === 'settings' ? 'nodes' as const : 'overview' as const };
          persistUi(next);
          return next;
        });
      }
      setBooting(false);
      document.documentElement.classList.remove('booting');
      try {
        const [settings] = await Promise.all([refreshSettings(), refreshAutopilot()]);
        await refreshScenarios().catch(() => undefined);
        void settings;
      } catch {

      }
    },
    [applyIdentity, persistUi, refreshAutopilot, refreshScenarios, refreshSettings],
  );

  const login = useCallback(
    async (username: string, password: string) => {
      await Api.auth.login({ username, password });
      const me = await Api.auth.me();
      const name = me.username || username;
      await enterApp(me);
      audit('Вход в панель', name);
    },
    [audit, enterApp],
  );

  const logout = useCallback(async () => {
    try {
      await Api.auth.logout();
    } catch {

    }
    showLogin();
  }, [showLogin]);

  useEffect(() => {
    setUnauthorizedHandler(showLogin);
    void (async () => {
      applyStoredTheme();
      setThemeState(getTheme());
      try {
        const me = await Api.auth.me();
        await enterApp(me);
      } catch {
        showLogin();
      }
    })();
  }, [enterApp, showLogin]);

  const autopilotLocked = autopilot?.status === 'running';
  const isSuperadmin = role === 'superadmin';
  const hasPermission = useCallback((permission: string) => isSuperadmin || permissions.includes(permission), [isSuperadmin, permissions]);

  const value = useMemo<AppContextValue>(
    () => ({
      booting,
      user,
      userId,
      displayName,
      role,
      permissions,
      hasPermission,
      isSuperadmin,
      page,
      ui,
      theme,
      settingsPayload,
      controlMode,
      baseDomain,
      autopilot,
      tasks,
      taskStats,
      clusters,
      controlBuild,
      scenarios,
      proxyPools,
      proxyMeta,
      auditLog,
      pendingLogTaskId,
      setPendingLogTaskId,
      goto,
      setUi,
      setSearchEngine,
      toggleTheme,
      login,
      logout,
      refreshSettings,
      refreshAutopilot,
      refreshTasks,
      refreshClusters,
      refreshScenarios,
      refreshProxy,
      setAutopilot,
      setTasks,
      setTaskStats,
      setClusters,
      setControlBuild,
      audit,
      autopilotLocked,
    }),
    [
      audit,
      auditLog,
      autopilot,
      autopilotLocked,
      baseDomain,
      booting,
      clusters,
      controlBuild,
      controlMode,
      displayName,
      goto,
      isSuperadmin,
      login,
      logout,
      page,
      pendingLogTaskId,
      proxyMeta,
      proxyPools,
      refreshAutopilot,
      refreshClusters,
      refreshProxy,
      refreshScenarios,
      refreshSettings,
      refreshTasks,
      role,
      permissions,
      hasPermission,
      scenarios,
      setSearchEngine,
      setUi,
      settingsPayload,
      taskStats,
      tasks,
      theme,
      toggleTheme,
      ui,
      user,
      userId,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp outside AppProvider');
  return ctx;
}
